/* Kareler — bırakma alanı, türev üretimi, yükleme, ve arşivin listesi.

   Bu ekran yeni bir hat kurmuyor, var olanı sürüyor. `packages/imaging` zaten
   şunları yapıyor ve gerçek karelerle sürüldü (tools/harness/upload.html):

     `planBatch`   → hangi dosya alınır, hangisi neden elenir
     `runPipeline` → Worker havuzu, aşama bildirimi, iptal
     `uploadPhoto` → imza → PUT → defter (`localStorage` ile devam)

   Panelin eklediği tek şey, hattın bittiği yerle veritabanı arasındaki adım:
   baytlar kovaya girdikten sonra `POST /api/admin/photos` satırı yazıyor.

   ── İki karar ───────────────────────────────────────────────

   **Kare hazır olur olmaz yükleniyor, yığın bitince değil.** `runPipeline`
   sonuçları `onProgress` üzerinden veriyor; 200 karelik bir klasörde
   hepsinin türevini bekleyip sonra yüklemek, iki yüz karenin baytını aynı
   anda bellekte tutmak demekti. Hazır olan gidiyor, sırası gelen türetiliyor.

   **Önce baytlar, sonra satır.** Tersi olsaydı yarıda kalan bir yığın,
   kovada karşılığı olmayan satırlar bırakırdı ve site o kareleri göstermeye
   çalışıp 404 alırdı. Bu sırayla en kötü hâl kovada sahipsiz bir nesne
   kalması — ikinci çalıştırma onu tamamlıyor ve kimse bir şey görmüyor.

   Yeniden çalıştırmak bu yüzden güvenli: defter tamamlanan katmanları
   hatırlıyor, `POST /api/admin/photos` aynı sağlama toplamını gördüğünde var
   olan kareyi döndürüyor. 200 karelik bir klasörü ikinci kez bırakmak kaza
   değil, olağan bir davranış (plan §5.2). */

import { localLedger, planBatch, runPipeline, summarize, uploadPhoto, workerRunner, xhrPut } from '@frawgraphy/imaging';
import type { DerivedPhoto, ItemState, Progress } from '@frawgraphy/imaging';
import { API_ORIGIN, photos } from '../api.ts';
import { originalExtFrom, photoIdFrom } from '../ids.ts';
import type { PhotoRow } from '../api.ts';
import { el, kb, replace, when } from '../dom.ts';
import type { AppContext } from '../main.ts';

/**
 * Aynı anda kaç kare yüklensin.
 *
 * Worker havuzundan ayrı bir sayı: türetme CPU işi, yükleme ağ işi. İkisini
 * aynı sayıya bağlamak, hızlı bir makinede yükleme kuyruğunu şişirir. Üç,
 * tek bir bağlantıyı doyurmaya yeter ve tarayıcının konak başına bağlantı
 * sınırının altında kalır.
 */
const UPLOAD_CONCURRENCY = 3;

type Line = { name: string; id: string; stage: string; note: string; tone: '' | 'ok' | 'bad' };

export function renderPhotos(host: HTMLElement, ctx: AppContext): void {
  const picker = el('input', {
    type: 'file',
    id: 'pick',
    multiple: true,
    accept: 'image/*',
    onchange: (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.files?.length) void start([...input.files], ctx, ui, archive);
    }
  });

  const zone = el(
    'div',
    { class: 'zone' },
    el('p', { text: 'Çekim klasörünü buraya bırakın — ham dosyalar ayıklanır.' }),
    el('label', { for: 'pick', text: 'Dosya seç' }),
    picker
  );
  zone.addEventListener('dragover', (event) => {
    event.preventDefault();
    zone.setAttribute('data-over', '');
  });
  zone.addEventListener('dragleave', () => zone.removeAttribute('data-over'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.removeAttribute('data-over');
    const files = [...(event.dataTransfer?.files ?? [])];
    if (files.length) void start(files, ctx, ui, archive);
  });

  const bar = el('span');
  const summary = el('p', { class: 'note', text: 'Bekleniyor.' });
  const cancel = el('button', { type: 'button', text: 'İptal', hidden: true });
  const rows = el('tbody');
  const archive = el('div');
  const ui: Ui = { bar, summary, cancel, rows };

  replace(
    host,
    el('h1', { text: 'Kareler' }),
    zone,
    el('div', { class: 'bar' }, bar),
    el('div', { class: 'row' }, summary, el('span', { class: 'spacer' }), cancel),
    el(
      'table',
      {},
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', { text: 'dosya' }),
          el('th', { text: 'kimlik' }),
          el('th', { text: 'aşama' }),
          el('th', { text: 'not' })
        )
      ),
      rows
    ),
    el('h2', { text: 'Arşiv' }),
    archive
  );

  void loadArchive(archive, ctx);
}

type Ui = { bar: HTMLElement; summary: HTMLElement; cancel: HTMLButtonElement; rows: HTMLElement };

async function start(files: File[], ctx: AppContext, ui: Ui, archive: HTMLElement): Promise<void> {
  /* Plan iki kez kuruluyor: bir kez burada (özet ve havuz boyu için), bir kez
     `runPipeline` içinde. Saf bir işlev, yalnızca dosya üstverisine bakıyor,
     yani ikinci çağrı aynı sonucu veriyor ve maliyeti yok sayılır. */
  const plan = planBatch(files);
  const { line, warning } = summarize(plan);
  ui.summary.replaceChildren(line);
  if (warning) ui.summary.append(el('span', { class: 'bad', text: ` ${warning}` }));

  const lines = new Map<string, Line>();
  const paint = (): void => ui.rows.replaceChildren(...[...lines.values()].map(drawLine));

  for (const skipped of plan.skipped) {
    lines.set(`atlandı:${skipped.file.name}`, {
      name: skipped.file.name,
      id: '—',
      stage: 'elendi',
      note: skipped.message,
      tone: 'bad'
    });
  }
  paint();

  if (plan.accepted.length === 0) return;

  const taken = new Set<string>();
  const idFor = new Map<string, string>();
  for (const file of plan.accepted) idFor.set(file.name, photoIdFrom(file.name, taken));

  const ledger = localLedger();
  const runner = workerRunner(
    () => new Worker(new URL('./worker.js', import.meta.url), { type: 'module' }),
    plan.concurrency
  );

  /* Hangi kare yüklemeye girdi — `onProgress` aynı öğeyi defalarca bildiriyor
     ve her bildirimde yeniden yüklemek 200 kareyi 200 kez göndermek olurdu. */
  const started = new Set<string>();
  const queue: (() => Promise<void>)[] = [];
  let running = 0;
  let stopped = false;
  let uploaded = 0;
  let failed = 0;

  const pump = (): void => {
    while (running < UPLOAD_CONCURRENCY && queue.length > 0 && !stopped) {
      const job = queue.shift()!;
      running += 1;
      void job().finally(() => {
        running -= 1;
        pump();
      });
    }
  };

  /** Kuyruk boşalana kadar bekler — yığın bitti demek için gerekir. */
  const drained = (): Promise<void> =>
    new Promise((resolve) => {
      const check = (): void => {
        if (running === 0 && queue.length === 0) resolve();
        else setTimeout(check, 120);
      };
      check();
    });

  const enqueue = (item: ItemState, derived: DerivedPhoto): void => {
    const file = plan.accepted.find((f) => f.name === item.name);
    if (!file) return;
    const id = idFor.get(item.name) ?? item.name;
    const row = lines.get(item.id);

    queue.push(async () => {
      if (stopped) return;
      try {
        await commit(id, file, derived, ledger, (stage, note, tone = '') => {
          if (row) Object.assign(row, { stage, note, tone });
          paint();
        });
        uploaded += 1;
      } catch (err) {
        failed += 1;
        if (row) {
          Object.assign(row, {
            stage: 'hata',
            note: err instanceof Error ? err.message : String(err),
            tone: 'bad'
          });
        }
        paint();
        /* Tek karenin düşmesi yığını durdurmuyor: 200 karelik bir yükte
           birinin başarısızlığı diğer 199'u iptal etmemeli. Oturum düştüyse
           `ctx.fail` zaten giriş ekranına götürür. */
        ctx.fail(err);
      }
    });
    pump();
  };

  ui.cancel.hidden = false;
  const run = runPipeline(plan.accepted, {
    runner,
    concurrency: plan.concurrency,
    onProgress: (progress: Progress) => {
      for (const item of progress.items) {
        const existing = lines.get(item.id);
        const row: Line = existing ?? {
          name: item.name,
          id: idFor.get(item.name) ?? '—',
          stage: item.stage,
          note: '',
          tone: ''
        };
        /* Yükleme aşamaları boru hattının aşamalarının üstüne yazılır:
           `hazır` olan kare artık yüklemenin elinde. */
        if (!started.has(item.id)) {
          row.stage = item.stage;
          if (item.error) {
            row.note = item.error;
            row.tone = 'bad';
          }
          if (item.duplicateOf) row.note = 'yığın içinde tekrar';
        }
        lines.set(item.id, row);

        if (item.stage === 'hazır' && item.photo && !started.has(item.id)) {
          started.add(item.id);
          enqueue(item, item.photo);
        }
      }
      ui.bar.style.width = `${Math.round(progress.ratio * 100)}%`;
      paint();
    }
  });

  ui.cancel.onclick = () => {
    stopped = true;
    run.cancel();
    ui.summary.replaceChildren('İptal edildi.');
  };

  try {
    await run.done;
    await drained();
    if (!stopped) {
      ui.summary.replaceChildren(
        `${uploaded} kare yüklendi${failed > 0 ? `, ${failed} kare düştü` : ''}.`
      );
      ctx.toast(`${uploaded} kare yüklendi.`, failed > 0 ? 'bad' : 'ok');
      /* Arşiv listesi tazelenmeli: yeni kareler albüm seçicisinde görünsün. */
      void loadArchive(archive, ctx);
    }
  } finally {
    ui.cancel.hidden = true;
    runner.dispose();
  }
}

/**
 * Baytları kovaya, satırı veritabanına yazar.
 *
 * Ölçüler türevi **üreten** yerden geliyor (tuval), sunucu baytları hiç
 * görmüyor — mimarinin kararı bu (packages/shared'daki `photoCommitInput`).
 */
async function commit(
  id: string,
  file: File,
  derived: DerivedPhoto,
  ledger: ReturnType<typeof localLedger>,
  report: (stage: string, note: string, tone?: '' | 'ok' | 'bad') => void
): Promise<void> {
  const originalExt = originalExtFrom(file.name);

  report('yükleniyor', '');
  const result = await uploadPhoto({
    photoId: id,
    originalExt,
    derived,
    original: file,
    signUrl: `${API_ORIGIN}/api/admin/uploads/sign`,
    /* Panel ayrı kökende; çerez ancak böyle gider (api.ts'teki gerekçe). */
    credentials: 'include',
    put: xhrPut,
    ledger,
    onProgress: (tier, loaded, total) =>
      report('yükleniyor', `${tier} %${Math.round((loaded / total) * 100)}`)
  });

  report('kaydediliyor', '');
  const row = await photos.commit({
    id,
    width: derived.width,
    height: derived.height,
    ratio: derived.ratio,
    originalExt,
    bytesBrowse: derived.browse.bytes,
    bytesFull: derived.full.bytes,
    bytesOriginal: file.size,
    ...(derived.lqip ? { lqip: derived.lqip } : {}),
    ...(derived.capturedAt ? { capturedAt: derived.capturedAt } : {}),
    /* Alt metin zorunlu. Dosya adı ilk hâli; yönetici sonradan düzeltir.
       Boş bırakıp "sonra doldururum" demek, 2 GB'lık bir arşivde
       erişilebilirliği kalıcı olarak kaybetmenin en kolay yoluydu. */
    alt: { tr: file.name.replace(/\.[^.]+$/, '') },
    ...(derived.checksum ? { checksum: derived.checksum } : {})
  });

  const skipped = result.skipped.length > 0 ? ` (${result.skipped.join(', ')} atlandı)` : '';
  report('yüklendi', row.duplicate ? `zaten vardı → ${row.id}` : `tamam${skipped}`, 'ok');
}

function drawLine(row: Line): HTMLElement {
  return el(
    'tr',
    {},
    el('td', { text: row.name }),
    el('td', { class: 'dim', text: row.id }),
    el('td', { class: row.tone, text: row.stage }),
    el('td', { class: 'dim', text: row.note })
  );
}

/* ── Arşiv listesi ──────────────────────────────────────── */

async function loadArchive(host: HTMLElement, ctx: AppContext): Promise<void> {
  replace(host, el('p', { class: 'note', text: 'Okunuyor…' }));
  try {
    const page = await photos.list({ limit: 60 });
    replace(host, drawArchive(page.photos));
  } catch (err) {
    replace(host, el('p', { class: 'err', text: 'Arşiv okunamadı.' }));
    ctx.fail(err);
  }
}

function drawArchive(rows: PhotoRow[]): HTMLElement {
  if (rows.length === 0) return el('p', { class: 'note', text: 'Henüz kare yok.' });
  return el(
    'table',
    {},
    el(
      'thead',
      {},
      el(
        'tr',
        {},
        el('th', { text: 'kimlik' }),
        el('th', { text: 'ölçü' }),
        el('th', { text: 'gezinme' }),
        el('th', { text: 'büyütme' }),
        el('th', { text: 'durum' }),
        el('th', { text: 'eklendi' })
      )
    ),
    el(
      'tbody',
      {},
      ...rows.map((row) =>
        el(
          'tr',
          {},
          el('td', { text: row.id }),
          el('td', { class: 'dim', text: `${row.width}×${row.height}` }),
          el('td', { text: kb(row.bytesBrowse) }),
          el('td', { text: kb(row.bytesFull) }),
          el('td', { class: row.status === 'ready' ? 'ok' : 'bad', text: row.status }),
          el('td', { class: 'dim', text: when(row.createdAt) })
        )
      )
    )
  );
}
